import { Site } from "@inrange/building-manager-api-client/models-site";
import {
  Column,
  Grid,
  SummaryBoxMetric,
  SummaryBoxMetrics,
} from "@inrange/theme-components";
import { OperationalSiteChart } from "@inrange/theme-components/charts";
import {
  formatLeaseTariff,
  formatLeaseTariffBillingFrequency,
  tariffToFractionalCurrency,
} from "@inrange/theme-components/formatting";
import React from "react";
import { getLatestActualsMonthIndex } from "../../views/sitelist-utils";
import {
  getAmountOfEnergyProcuredChartData,
  getCostOfEnergyProcuredChartData,
  getOperationalEnergySoldChartData,
  getOperationalRevenueChartData,
} from "./chartDataFormatting";
import {
  generateProcuredMetrics,
  generateRevenueMetrics,
  getTariffHeader,
} from "./operational-utils";
import OperationalDemoMetrics from "./OperationalDemoMetrics";

const OperationalSummaryOwnerOccupier: React.FC<{
  site: Site;
  isOwnerOccupier: boolean;
  orgDemo: boolean;
}> = ({ site, isOwnerOccupier, orgDemo }) => {
  const costOfEnergyProcuredChartData = getCostOfEnergyProcuredChartData(
    site,
    isOwnerOccupier,
    site.activeInvestmentModel
  );
  const amountOfEnergyProcuredChartData =
    getAmountOfEnergyProcuredChartData(site);
  const revenueChartData = getOperationalRevenueChartData(
    site,
    isOwnerOccupier,
    isOwnerOccupier && site.activeInvestmentModel === "license",
    site.activeInvestmentModel
  );
  const energySoldChartData = getOperationalEnergySoldChartData(
    site,
    isOwnerOccupier && site.activeInvestmentModel === "license"
  );

  const lastMonthIndex = getLatestActualsMonthIndex(site);
  const lastMonthEnergyFinancials =
    lastMonthIndex !== undefined
      ? site.energyFinancialsMonthly[lastMonthIndex]
      : undefined;
  const isOnsitePvLease =
    site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease";
  const procuredMetrics = generateProcuredMetrics(
    site,
    lastMonthEnergyFinancials?.energy.actual,
    lastMonthEnergyFinancials?.financial.actual,
    isOwnerOccupier,
    site.activeInvestmentModel,
    isOnsitePvLease
  );
  const revenueMetrics = generateRevenueMetrics(
    site,
    lastMonthEnergyFinancials?.energy.actual,
    lastMonthEnergyFinancials?.financial.actual,
    isOwnerOccupier,
    isOnsitePvLease,
    site.activeInvestmentModel
  );

  const tariffMetrics: SummaryBoxMetric[] = [];
  if (isOnsitePvLease) {
    tariffMetrics.push({
      value: formatLeaseTariff(
        site.tariffConfig.onsitePPA.tariff,
        site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency,
        site.currencyCode
      ),
      valueSubText: formatLeaseTariffBillingFrequency(
        site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency
      ),
      dataTestId: "tenant-tariff",
    });
  } else if (site.energyFlowAnnual.behindMeter > 0) {
    tariffMetrics.push({
      value: tariffToFractionalCurrency(
        site.tariffConfig.onsitePPA.tariff,
        site.currencyCode
      ),
      valueSubText: "On-site",
      dataTestId: "tenant-tariff",
    });
  }
  if (site.energyFlowAnnual.networkImport > 0) {
    tariffMetrics.push({
      value: tariffToFractionalCurrency(
        site.tariffConfig.networkImportTariff,
        site.currencyCode
      ),
      valueSubText: "InRange network",
    });
  }
  if (
    site.energyFlowAnnual.networkExport + site.energyFlowAnnual.exported >
    0
  ) {
    tariffMetrics.push({
      value: tariffToFractionalCurrency(
        site.blendedExportTariff,
        site.currencyCode
      ),
      valueSubText: "Avg export",
    });
  }
  const tariffHeader = getTariffHeader(isOnsitePvLease, tariffMetrics.length);
  const chartsHeight = "416px";

  return (
    <Grid $cols={1} $colwidth={"370px 1fr"} $gap={"10px"} $margin={"5px"}>
      <Column $height={"fit-content"}>
        <OperationalDemoMetrics site={site} orgDemo={orgDemo} />
        <SummaryBoxMetrics
          metrics={tariffMetrics}
          header={tariffHeader}
          grid={true}
        />
        <SummaryBoxMetrics
          metrics={procuredMetrics}
          header={"Procured previous month"}
          grid={false}
        />
        <SummaryBoxMetrics
          metrics={revenueMetrics}
          header={"Sold previous month"}
          grid={false}
        />
      </Column>
      <Column $height={"100%"}>
        <OperationalSiteChart
          header="Cost of energy procured"
          chartData={costOfEnergyProcuredChartData}
          toolTipText="The cost of energy procured from InRange during a given month, where energy can be procured from the on-site installation or from the InRange network, at the relevant tariffs. For months where no data exists, the data reflects the forecast cost. Does not include VAT."
          dataType="currency"
          currencyCode={site.currencyCode}
          height={chartsHeight}
        />
        <OperationalSiteChart
          header="Amount of energy procured"
          chartData={amountOfEnergyProcuredChartData}
          toolTipText="The amount of energy in kWh procured from InRange during a given month, where energy can be procured from the on-site installation or from the InRange network. For months where no data exists, the data reflects the forecast amount."
          dataType="energy"
          height={chartsHeight}
        />
        <OperationalSiteChart
          header="Revenue"
          chartData={revenueChartData}
          toolTipText="The revenue generated from energy sold after the given revenue share during a given month, where energy can be sold on-site or exported to the InRange network, at the relevant tariffs. For months where no data exists, the data reflects the forecast revenue. Does not include VAT."
          dataType="currency"
          currencyCode={site.currencyCode}
          height={chartsHeight}
        />
        <OperationalSiteChart
          header="Energy sold"
          chartData={energySoldChartData}
          toolTipText="The amount of energy sold in kWh during a given month, where energy can be sold on-site or exported to the InRange network. For months where no data exists, the data reflects the forecast amount."
          dataType="energy"
          height={chartsHeight}
        />
      </Column>
    </Grid>
  );
};

export default OperationalSummaryOwnerOccupier;
